import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const shipBg = "/asset/ship_image.jpg";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

// View modes: ship image | live position map | path playback map
const VIEW = { SHIP: "ship", LIVE_MAP: "live_map", PATH_MAP: "path_map" };

const safeVal = (v, d = 2, fb = "—") => (v != null ? Number(v).toFixed(d) : fb);
const fmtDate = (d) => {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const todayStr = fmtDate(new Date());

// ─── DatePicker ───────────────────────────────────────────────────────────────
function DatePicker({ value, onChange, min, max, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const toDate = (s) => { if (!s) return new Date(); const [y,m,d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
  const display = (s) => { if (!s) return "Select"; const [y,m,d] = s.split("-"); return `${d}-${m}-${y}`; };
  const cur = toDate(value);
  const [viewYear, setViewYear] = useState(cur.getFullYear());
  const [viewMonth, setViewMonth] = useState(cur.getMonth());
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y + 1)) : setViewMonth(m => m + 1);
  const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const firstDay = (y, m) => new Date(y, m, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDay(viewYear, viewMonth); i++) cells.push(null);
  for (let d = 1; d <= daysInMonth(viewYear, viewMonth); d++) cells.push(d);
  const cellDate   = (d) => fmtDate(new Date(viewYear, viewMonth, d));
  const isSelected = (d) => d && cellDate(d) === value;
  const isDisabled = (d) => d && ((min && cellDate(d) < min) || (max && cellDate(d) > max));
  const isToday    = (d) => d && cellDate(d) === todayStr;
  const selectDay  = (d) => { if (!d || isDisabled(d)) return; onChange(cellDate(d)); setOpen(false); };
  const openPicker = () => { const c = toDate(value); setViewYear(c.getFullYear()); setViewMonth(c.getMonth()); setOpen(v => !v); };

  const DROPDOWN_HEIGHT = 300;
  const getDropdownStyle = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return { position: "fixed", top: 100, left: 100, zIndex: 999999 };
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < DROPDOWN_HEIGHT + 10;
    return {
      position: "fixed",
      top: openUpward ? rect.top - DROPDOWN_HEIGHT - 6 : rect.bottom + 6,
      left: Math.min(rect.left, window.innerWidth - 260),
      zIndex: 999999, background: "#fff", border: "1px solid #e2e8f0",
      borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
      padding: 12, width: 248, userSelect: "none",
    };
  };

  return (
    <div ref={ref} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {label && <span style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>}
      <button onClick={openPicker} style={{
        fontSize: 11, padding: "5px 10px", borderRadius: 6, border: "1px solid #cbd5e1",
        color: "#1e293b", background: "#fff", cursor: "pointer", width: 136,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
        fontFamily: "inherit", fontWeight: 500,
      }}>
        <span>{display(value)}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </button>
      {open && (
        <div style={getDropdownStyle()}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <button onClick={prevMonth} style={navBtn}>‹</button>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{MONTHS[viewMonth]} {viewYear}</span>
            <button onClick={nextMonth} style={navBtn}>›</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, marginBottom: 4 }}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
              <div key={d} style={{ fontSize: 9, color: "#94a3b8", textAlign: "center", fontWeight: 600, padding: "2px 0" }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
            {cells.map((d, i) => (
              <div key={i} onClick={() => selectDay(d)} style={{
                height: 30, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: isSelected(d) ? 700 : 400,
                background: isSelected(d) ? "#1e40af" : isToday(d) ? "#eff6ff" : "transparent",
                color: !d ? "transparent" : isSelected(d) ? "#fff" : isDisabled(d) ? "#cbd5e1" : isToday(d) ? "#1e40af" : "#1e293b",
                cursor: (!d || isDisabled(d)) ? "default" : "pointer",
                border: isToday(d) && !isSelected(d) ? "1px solid #bfdbfe" : "none",
              }}>{d || ""}</div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
            <button onClick={() => { onChange(todayStr); setOpen(false); }} style={footerBtn("#2563eb")}>Today</button>
            <button onClick={() => setOpen(false)} style={footerBtn("#64748b")}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
const navBtn = { background: "none", border: "none", cursor: "pointer", color: "#1e293b", fontSize: 18, padding: "0 6px", lineHeight: 1, borderRadius: 4 };
const footerBtn = (c) => ({ fontSize: 11, color: c, background: "none", border: "none", cursor: "pointer", fontWeight: 600 });

// ─── UptimeDisplay ────────────────────────────────────────────────────────────
function parseUptime(raw) {
  if (!raw || raw === "—") return null;
  const s = String(raw).trim();
  if (s.startsWith("P")) { const m = s.match(/P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?/i); if (m) return { days: +(m[1]||0), hours: +(m[2]||0), minutes: +(m[3]||0) }; }
  const dm = s.match(/(\d+)\s+days?,\s*(\d+):(\d+)/i); if (dm) return { days: +dm[1], hours: +dm[2], minutes: +dm[3] };
  const tm = s.match(/^(\d+):(\d+):(\d+)$/); if (tm) return { days: 0, hours: +tm[1], minutes: +tm[2] };
  return { raw: s };
}
function UptimeDisplay({ uptime }) {
  const p = parseUptime(uptime);
  if (!p) return <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>;
  if (p.raw) return <span style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>{p.raw}</span>;
  const hh = String(p.hours).padStart(2, "0"), mm = String(p.minutes).padStart(2, "0");
  return <div style={{ lineHeight: 1.25 }}>
    <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{p.days}</span>
    <span style={{ fontSize: 10, color: "#94a3b8" }}>d </span>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{hh}</span>
    <span style={{ fontSize: 10, color: "#94a3b8" }}>h </span>
    <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{mm}</span>
    <span style={{ fontSize: 10, color: "#94a3b8" }}>m</span>
  </div>;
}

// ─── Map helpers ──────────────────────────────────────────────────────────────
function RecenterMap({ lat, lng, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], map.getZoom(), { animate: true });
  }, [lat, lng, trigger, map]);
  return null;
}

function FitPathBounds({ shipPath, trigger }) {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    if (shipPath.length > 1 && prev.current !== trigger) {
      prev.current = trigger;
      const bounds = L.latLngBounds(shipPath.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40], animate: true });
    }
  }, [shipPath, trigger, map]);
  return null;
}

function ZoomControls() {
  const map = useMap();
  const btn = { width: 32, height: 32, borderRadius: 7, border: "0.5px solid #cbd5e1", background: "rgba(255,255,255,0.95)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 5px rgba(0,0,0,.15)" };
  return (
    <div style={{ position: "absolute", bottom: 60, right: 12, zIndex: 1000, display: "flex", flexDirection: "column", gap: 4 }}>
      <button style={btn} onClick={() => map.zoomIn()}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
      <button style={btn} onClick={() => map.zoomOut()}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
    </div>
  );
}

function createShipIcon(heading) {
  const rot = (heading || 270) - 270;
  return L.divIcon({
    className: "",
    html: `<svg width="30" height="30" viewBox="-15 -15 30 30" xmlns="http://www.w3.org/2000/svg"
             style="transform:rotate(${rot}deg);display:block;filter:drop-shadow(0 2px 5px rgba(0,0,0,.5))">
            <polygon points="0,-14 10,9 0,5 -10,9" fill="#1d4ed8" stroke="#fff" stroke-width="2.2"/>
          </svg>`,
    iconSize: [30, 30], iconAnchor: [15, 15],
  });
}

function dotIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div style="width:13px;height:13px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.4)"></div>`,
    iconSize: [13, 13], iconAnchor: [6, 6],
  });
}

function Spinner({ color = "#2563eb" }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5"
      style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
  );
}

// Shared "Show Ship" back-button
function ShowShipBtn({ onClick }) {
  return (
    <button onClick={onClick} style={{
      position: "absolute", top: 10, right: 10, zIndex: 1000,
      background: "#1e40af", color: "#fff", border: "none", borderRadius: 7,
      padding: "7px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer",
      display: "flex", alignItems: "center", gap: 6,
      boxShadow: "0 2px 10px rgba(30,64,175,0.4)",
      transition: "opacity 0.15s",
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2">
        <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
      </svg>
      Show Ship
    </button>
  );
}

// ─── Section accordion ────────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ borderBottom: "0.5px solid #e2e8f0", padding: "10px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => setOpen(v => !v)}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 5 }}>
          {icon} {title}
        </span>
        <span style={{ fontSize: 10, color: "#94a3b8", transform: open ? "" : "rotate(180deg)", transition: "transform .2s", display: "inline-block" }}>▲</span>
      </div>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

const CARD    = { background: "#f8fafc", borderRadius: 8, padding: "7px 10px", border: "0.5px solid #e2e8f0" };
const CARDLBL = { fontSize: 9, color: "#94a3b8", display: "block", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 };

const pbBtn = {
  width: 30, height: 30, borderRadius: 6, border: "0.5px solid #e2e8f0",
  background: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
  justifyContent: "center", flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
};

// ─── PathBar ──────────────────────────────────────────────────────────────────
function PathBar({ startDate, endDate, todayStr, maxEndDate, handleStartDate, setEndDate, fetchLoading, fetchPath, fetchStatus, shipPath, playing, togglePlay }) {
  return (
    <div style={{
      position: "absolute", bottom: 48, left: 0, right: 0, zIndex: 25,
      background: "rgba(2,10,28,0.88)", borderTop: "0.5px solid rgba(255,255,255,0.07)",
      padding: "8px 14px", display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap",
    }}>
      <DatePicker label="Path Start" value={startDate} onChange={handleStartDate} max={todayStr}/>
      <span style={{ color: "#475569", paddingBottom: 4, fontSize: 16 }}>→</span>
      <DatePicker label={<>Path End <span style={{ color: "#f59e0b" }}>(max 7d)</span></>} value={endDate} min={startDate} max={maxEndDate} onChange={setEndDate}/>
      <button onClick={fetchPath} disabled={fetchLoading} style={{
        background: fetchLoading ? "#3b5bdb" : "#1e40af", color: "#fff", border: "none", borderRadius: 6,
        padding: "5px 14px", fontSize: 11, fontWeight: 600, cursor: fetchLoading ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", gap: 5, opacity: fetchLoading ? 0.75 : 1, alignSelf: "flex-end",
      }}>
        {fetchLoading ? <><Spinner color="#fff"/> Fetching…</> : <>🗺 Fetch Path</>}
      </button>
      {fetchStatus && (
        <span style={{
          fontSize: 10, borderRadius: 5, padding: "3px 8px",
          background: fetchStatus.type === "error" ? "rgba(239,68,68,0.18)" : fetchStatus.type === "success" ? "rgba(22,163,74,0.18)" : "rgba(37,99,235,0.18)",
          color: fetchStatus.type === "error" ? "#fca5a5" : fetchStatus.type === "success" ? "#86efac" : "#93c5fd",
          border: `0.5px solid ${fetchStatus.type === "error" ? "#fca5a5" : fetchStatus.type === "success" ? "#86efac" : "#93c5fd"}`,
          display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", alignSelf: "flex-end",
        }}>
          {fetchStatus.type === "error" ? "⚠" : fetchStatus.type === "success" ? "✓" : "ℹ"} {fetchStatus.msg}
        </span>
      )}
      {shipPath.length > 0 && (
        <button onClick={togglePlay} style={{
          marginLeft: "auto", background: playing ? "#dc2626" : "#16a34a", color: "#fff", border: "none",
          borderRadius: 6, padding: "5px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5, alignSelf: "flex-end",
        }}>
          {playing ? "⏸ Pause" : "▶ Play Path"}
        </button>
      )}
    </div>
  );
}

// ─── PlaybackBar ──────────────────────────────────────────────────────────────
function PlaybackBar({ shipPath, playing, togglePlay, restartPb, safeIdx, sliderSeek, curPoint, speedIdx, cycleSpeed }) {
  const SPEED_LABELS = ["1×","2×","4×","8×"];
  return (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 30,
      background: "#ffffff", borderTop: "1px solid #e2e8f0",
      padding: "8px 14px", display: "flex", alignItems: "center", gap: 10,
      boxShadow: "0 -2px 8px rgba(0,0,0,0.06)", height: 48,
    }}>
      <button style={pbBtn} onClick={togglePlay} disabled={!shipPath.length}>
        {playing
          ? <svg width="12" height="12" viewBox="0 0 24 24" fill="#1e293b"><rect x="5" y="3" width="5" height="18" rx="1"/><rect x="14" y="3" width="5" height="18" rx="1"/></svg>
          : <svg width="12" height="12" viewBox="0 0 24 24" fill="#1e293b"><polygon points="5,3 20,12 5,21"/></svg>
        }
      </button>
      <button style={pbBtn} onClick={restartPb} disabled={!shipPath.length}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2.3">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
        </svg>
      </button>
      <input type="range" min={0} max={Math.max(0, shipPath.length - 1)} value={safeIdx} step={1}
        onChange={e => sliderSeek(e.target.value)} disabled={!shipPath.length}
        style={{ flex: 1, accentColor: "#3b82f6", cursor: "pointer" }}/>
      <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", minWidth: 110, textAlign: "right" }}>
        {shipPath.length > 0 ? `${safeIdx + 1}/${shipPath.length} · ${curPoint?.time?.slice(11) || "--:--"}` : "No data"}
      </span>
      <button onClick={cycleSpeed} style={{
        fontSize: 11, fontWeight: 600, background: "#eff6ff", border: "0.5px solid #bfdbfe",
        borderRadius: 5, padding: "3px 9px", color: "#1e40af", cursor: "pointer", flexShrink: 0,
      }}>
        {SPEED_LABELS[speedIdx]}
      </button>
    </div>
  );
}

// ─── LiveMapView — current ship position only ─────────────────────────────────
function LiveMapView({ telemetry, onShowShip }) {
  const lat = telemetry?.location?.lat;
  const lng = telemetry?.location?.lon;
  const hasPos = lat != null && lng != null;

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <MapContainer
        style={{ height: "100%", width: "100%" }}
        center={hasPos ? [lat, lng] : [20, 80]}
        zoom={hasPos ? 8 : 4}
        zoomControl={false}
      >
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
        <ZoomControls/>
        {hasPos && <RecenterMap lat={lat} lng={lng} trigger="live"/>}
        {hasPos && (
          <Marker position={[lat, lng]} icon={createShipIcon(telemetry?.navigation?.heading)}>
            <Popup>
              <strong>Live Position</strong><br/>
              {Number(lat).toFixed(4)}°, {Number(lng).toFixed(4)}°
              {telemetry?.navigation?.sog != null && <><br/>SOG: {safeVal(telemetry.navigation.sog, 1)} kn</>}
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Live badge */}
      <div style={{
        position: "absolute", top: 10, left: 10, zIndex: 1000,
        background: "rgba(255,255,255,0.97)", borderRadius: 8, padding: "7px 12px",
        border: "0.5px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        display: "flex", alignItems: "center", gap: 7,
      }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a", display: "inline-block", boxShadow: "0 0 0 3px rgba(22,163,74,0.2)", flexShrink: 0, animation: "pulse 2s infinite" }}/>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#1e293b" }}>Live Position</span>
        {hasPos && <span style={{ fontSize: 10, color: "#64748b" }}>{Number(lat).toFixed(3)}°, {Number(lng).toFixed(3)}°</span>}
      </div>

      <ShowShipBtn onClick={onShowShip}/>

      {!hasPos && (
        <div style={{ position: "absolute", inset: 0, zIndex: 500, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(219,234,254,0.85)", gap: 8, pointerEvents: "none" }}>
          <div style={{ fontSize: 32 }}>📡</div>
          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>No live position</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Check telemetry data</div>
        </div>
      )}
    </div>
  );
}

// ─── PathMapView — full path playback ─────────────────────────────────────────
function PathMapView({
  shipPath, safeIdx, curPoint, visitedPath, remainingPath,
  playing, togglePlay, restartPb, sliderSeek, speedIdx, cycleSpeed,
  startDate, endDate, todayStr, maxEndDate,
  handleStartDate, setEndDate, fetchLoading, fetchPath, fetchStatus,
  onShowShip, pathFitTrigger,
}) {
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <MapContainer
        style={{ height: "100%", width: "100%" }}
        center={curPoint?.lat != null ? [curPoint.lat, curPoint.lng] : [20, 80]}
        zoom={curPoint?.lat != null ? 7 : 4}
        zoomControl={false}
      >
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
        <ZoomControls/>
        <FitPathBounds shipPath={shipPath} trigger={pathFitTrigger}/>
        {playing && curPoint?.lat != null && <RecenterMap lat={curPoint.lat} lng={curPoint.lng} trigger={safeIdx}/>}

        {remainingPath.length > 1 && (
          <Polyline positions={remainingPath} pathOptions={{ color: "#94a3b8", weight: 2, opacity: 0.4, dashArray: "7 5" }}/>
        )}
        {visitedPath.length > 1 && (
          <Polyline positions={visitedPath} pathOptions={{ color: "#2563eb", weight: 3.5, opacity: 0.9 }}/>
        )}

        {shipPath.length > 0 && (
          <Marker position={[shipPath[0].lat, shipPath[0].lng]} icon={dotIcon("#16a34a")}>
            <Popup><strong>Start</strong><br/>{shipPath[0].lat.toFixed(4)}°, {shipPath[0].lng.toFixed(4)}°{shipPath[0].time && <><br/>{shipPath[0].time}</>}</Popup>
          </Marker>
        )}
        {shipPath.length > 1 && (
          <Marker position={[shipPath[shipPath.length-1].lat, shipPath[shipPath.length-1].lng]} icon={dotIcon("#dc2626")}>
            <Popup><strong>End</strong><br/>{shipPath[shipPath.length-1].lat.toFixed(4)}°, {shipPath[shipPath.length-1].lng.toFixed(4)}°</Popup>
          </Marker>
        )}
        {curPoint?.lat != null && (
          <Marker position={[curPoint.lat, curPoint.lng]} icon={createShipIcon(curPoint?.heading)}>
            <Popup>
              <strong>Position</strong><br/>
              {curPoint.lat.toFixed(4)}°, {curPoint.lng.toFixed(4)}°
              {curPoint.speed != null && <><br/>Speed: {curPoint.speed} kn</>}
              {curPoint.heading != null && <><br/>Heading: {curPoint.heading}°</>}
              {curPoint.time && <><br/>{curPoint.time}</>}
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Legend */}
      {shipPath.length > 0 && (
        <div style={{
          position: "absolute", top: 10, left: 10, zIndex: 1000,
          background: "rgba(255,255,255,0.97)", borderRadius: 8, padding: "8px 12px",
          border: "0.5px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          display: "flex", flexDirection: "column", gap: 6,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#1e293b", marginBottom: 2 }}>Path Playback</div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke="#2563eb" strokeWidth="3.5"/></svg>
            <span style={{ fontSize: 10, color: "#1e293b", fontWeight: 500 }}>Visited</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5 3"/></svg>
            <span style={{ fontSize: 10, color: "#64748b" }}>Remaining</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#16a34a", border: "2px solid #fff", boxShadow: "0 1px 3px rgba(0,0,0,.3)", flexShrink: 0 }}/>
            <span style={{ fontSize: 10, color: "#64748b" }}>Start</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#dc2626", border: "2px solid #fff", boxShadow: "0 1px 3px rgba(0,0,0,.3)", flexShrink: 0 }}/>
            <span style={{ fontSize: 10, color: "#64748b" }}>End</span>
          </div>
        </div>
      )}

      <ShowShipBtn onClick={onShowShip}/>

      {shipPath.length === 0 && (
        <div style={{ position: "absolute", inset: 0, zIndex: 500, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(219,234,254,0.85)", gap: 8, pointerEvents: "none" }}>
          <div style={{ fontSize: 32 }}>🗺️</div>
          <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>No path loaded</div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Use the bar below to fetch a path</div>
        </div>
      )}

      <PathBar
        startDate={startDate} endDate={endDate} todayStr={todayStr} maxEndDate={maxEndDate}
        handleStartDate={handleStartDate} setEndDate={setEndDate}
        fetchLoading={fetchLoading} fetchPath={fetchPath} fetchStatus={fetchStatus}
        shipPath={shipPath} playing={playing} togglePlay={togglePlay}
      />
      <PlaybackBar
        shipPath={shipPath} playing={playing} togglePlay={togglePlay}
        restartPb={restartPb} safeIdx={safeIdx} sliderSeek={sliderSeek}
        curPoint={curPoint} speedIdx={speedIdx} cycleSpeed={cycleSpeed}
      />
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function VesselDashboard() {
  const [activeTab,        setActiveTab]       = useState("Crew");
  const [viewMode,         setViewMode]        = useState(VIEW.SHIP);
  const [telemetryDate,    setTelemetryDate]   = useState(todayStr);
  const [startDate,        setStartDate]       = useState(todayStr);
  const [endDate,          setEndDate]         = useState(todayStr);
  const [shipPath,         setShipPath]        = useState([]);
  const [fetchLoading,     setFetchLoading]    = useState(false);
  const [fetchStatus,      setFetchStatus]     = useState(null);
  const [playIdx,          setPlayIdx]         = useState(0);
  const [playing,          setPlaying]         = useState(false);
  const [speedIdx,         setSpeedIdx]        = useState(0);
  const [telemetry,        setTelemetry]       = useState(null);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const [utc,              setUtc]             = useState("--:--:--");
  const [pathFitTrigger,   setPathFitTrigger]  = useState(0);

  const timerRef = useRef(null);
  const SPEEDS   = [1, 2, 4, 8];

  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setUtc([n.getUTCHours(), n.getUTCMinutes(), n.getUTCSeconds()].map(v => String(v).padStart(2, "0")).join(":"));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const maxEndDate = (() => { const d = new Date(startDate); d.setDate(d.getDate() + 7); return fmtDate(d); })();

  const handleStartDate = (val) => {
    setStartDate(val);
    const max = new Date(val); max.setDate(max.getDate() + 7);
    const maxStr = fmtDate(max);
    if (endDate < val) setEndDate(val);
    else if (endDate > maxStr) setEndDate(maxStr);
  };

  const fetchTelemetry = useCallback(async (date) => {
    if (!date) return;
    setTelemetryLoading(true);
    try {
      const r = await fetch(`https://api.hanoch.in/api/vessel-telemetry/?date=${date}`);
      if (!r.ok) throw new Error();
      const j = await r.json();
      setTelemetry(j.data);
    } catch { setTelemetry(null); }
    finally { setTelemetryLoading(false); }
  }, []);
  useEffect(() => { fetchTelemetry(telemetryDate); }, [telemetryDate, fetchTelemetry]);

  const stopTimer = useCallback(() => { clearInterval(timerRef.current); timerRef.current = null; }, []);

  const fetchPath = useCallback(async () => {
    if (!startDate || !endDate) { setFetchStatus({ type: "error", msg: "Both dates required." }); return; }
    setFetchLoading(true);
    setFetchStatus({ type: "info", msg: "Fetching vessel path…" });
    stopTimer(); setPlaying(false); setPlayIdx(0);
    setViewMode(VIEW.PATH_MAP); // switch to path map immediately
    try {
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const r = await fetch(`https://api.hanoch.in/api/vessel-path/?${qs}`);
      if (!r.ok) { let e = `HTTP ${r.status}`; try { const b = await r.json(); e = b.error || b.detail || e; } catch (_) {} throw new Error(e); }
      const j = await r.json();
      if (!j?.data?.length) { setShipPath([]); setFetchStatus({ type: "error", msg: "No data for range." }); return; }
      const pts = j.data.map(pt => ({
        lat: pt.lat, lng: pt.lon ?? pt.lng,
        time: pt.timestamp ? pt.timestamp.slice(0, 16).replace("T", " ") : "",
        speed: pt.speed ?? null, heading: pt.heading ?? null,
      }));
      setShipPath(pts);
      setPathFitTrigger(t => t + 1);
      setFetchStatus({ type: "success", msg: `${j.count ?? pts.length} points loaded` });
    } catch (err) {
      setShipPath([]);
      setFetchStatus({ type: "error", msg: err.message });
    } finally { setFetchLoading(false); }
  }, [startDate, endDate, stopTimer]);

  const startPlay = useCallback(() => {
    if (!shipPath.length) return;
    setViewMode(VIEW.PATH_MAP); // auto-switch to path map
    if (playIdx >= shipPath.length - 1) setPlayIdx(0);
    setPlaying(true);
    timerRef.current = setInterval(() => {
      setPlayIdx(prev => {
        if (prev >= shipPath.length - 1) { stopTimer(); setPlaying(false); return prev; }
        return prev + 1;
      });
    }, Math.round(600 / SPEEDS[speedIdx]));
  }, [playIdx, speedIdx, shipPath.length, stopTimer]);

  const pausePlay  = useCallback(() => { stopTimer(); setPlaying(false); }, [stopTimer]);
  const togglePlay = useCallback(() => (playing ? pausePlay() : startPlay()), [playing, pausePlay, startPlay]);
  const restartPb  = useCallback(() => { pausePlay(); setPlayIdx(0); }, [pausePlay]);
  const sliderSeek = useCallback((v) => { pausePlay(); setPlayIdx(Number(v)); }, [pausePlay]);
  const cycleSpeed = useCallback(() => {
    const n = (speedIdx + 1) % SPEEDS.length; setSpeedIdx(n);
    if (playing) { stopTimer(); setPlaying(false); }
  }, [speedIdx, playing, stopTimer]);

  useEffect(() => () => stopTimer(), [stopTimer]);

  const safeIdx       = Math.min(playIdx, Math.max(0, shipPath.length - 1));
  const curPoint      = shipPath[safeIdx] || null;
  const visitedPath   = shipPath.slice(0, safeIdx + 1).map(p => [p.lat, p.lng]);
  const remainingPath = shipPath.slice(safeIdx).map(p => [p.lat, p.lng]);

  const chips = telemetry ? [
    { label: "SOG",       value: safeVal(telemetry.navigation?.sog, 2),  unit: "Kn" },
    { label: "STW",       value: safeVal(telemetry.navigation?.stw, 2),  unit: "Kn" },
    { label: "COG",       value: telemetry.navigation?.status ?? "—",    green: true },
    { label: "Draft Fwd", value: `${safeVal(telemetry.draft?.fwd)} m` },
    { label: "Draft Aft", value: `${safeVal(telemetry.draft?.aft)} m` },
  ] : [];

  return (
    <React.Fragment>
      <div className="page-content">
        <div style={{ fontFamily: "'Segoe UI',sans-serif", background: "#f8fafc", height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: "#fff", border: "0.5px solid #e2e8f0", boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>

            {/* HEADER */}
            <header style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "4px 16px", borderBottom: "0.5px solid #e2e8f0",
              background: "#fff", flexWrap: "wrap", gap: 8, flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <button style={{ background: "#1e40af", color: "#fff", border: "none", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  🚢 MV Atlas Prime ▾
                </button>
                {["Crew","Reports"].map(t => (
                  <button key={t} style={{
                    background: activeTab === t ? "#f1f5f9" : "transparent",
                    border: "0.5px solid #e2e8f0", borderRadius: 8, padding: "5px 12px", fontSize: 12, cursor: "pointer",
                    color: activeTab === t ? "#1e293b" : "#64748b",
                  }} onClick={() => setActiveTab(t)}>
                    {t === "Crew" ? "👥 " : "📋 "}{t}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#f8fafc", border: "0.5px solid #e2e8f0", borderRadius: 8, padding: "4px 10px" }}>
                  <DatePicker label="" value={telemetryDate} onChange={setTelemetryDate} max={todayStr}/>
                  {telemetryLoading && <Spinner/>}
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>Up Time</span>
                  <UptimeDisplay uptime={telemetry?.uptime}/>
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>Time UTC</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>{utc}</span>
                </div>
              </div>
            </header>

            {/* BODY */}
            <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

              {/* MAIN PANE */}
              <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>

                {/* ══ SHIP IMAGE ══ */}
                {viewMode === VIEW.SHIP && (
                  <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${shipBg})`, backgroundSize: "cover", backgroundPosition: "center", overflow: "visible" }}>

                    {/* HUD chips top-left */}
                    {chips.length > 0 && (
                      <div style={{
                        position: "absolute", top: 12, left: 12, zIndex: 20,
                        display: "flex", alignItems: "center",
                        background: "rgba(0,50,60,0.72)", borderRadius: 11, padding: "8px 14px",
                        border: "0.5px solid rgba(255,255,255,0.12)",
                      }}>
                        {chips.map((c, i) => (
                          <div key={i} style={{
                            display: "flex", flexDirection: "column",
                            paddingRight: i === chips.length - 1 ? 0 : 13,
                            marginRight:  i === chips.length - 1 ? 0 : 13,
                            borderRight:  i === chips.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.15)",
                            whiteSpace: "nowrap",
                          }}>
                            <span style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{c.label}</span>
                            <span style={{ fontSize: c.unit ? 15 : 12, fontWeight: 600, color: c.green ? "#4ade80" : "#fff" }}>
                              {c.value}
                              {c.unit && <small style={{ fontSize: 10, color: "#94a3b8", marginLeft: 3 }}>{c.unit}</small>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* HUD weather top-right */}
                    <div style={{
                      position: "absolute", top: 12, right: 12, zIndex: 20,
                      display: "flex", alignItems: "center",
                      background: "rgba(0,50,60,0.72)", borderRadius: 11, padding: "8px 14px",
                      border: "0.5px solid rgba(255,255,255,0.12)",
                    }}>
                      {[
                        { label: "Wind",      value: telemetry ? `${safeVal(telemetry.weather?.wind_direction, 0)}°` : "—" },
                        { label: "Sea State", value: telemetry?.weather?.sea_state ?? "—" },
                        { label: "Position",  value: telemetry ? `${safeVal(telemetry.location?.lat, 3)}, ${safeVal(telemetry.location?.lon, 3)}` : "—" },
                      ].map((w, i, arr) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 8,
                          paddingRight: i === arr.length - 1 ? 0 : 13,
                          marginRight:  i === arr.length - 1 ? 0 : 13,
                          borderRight:  i === arr.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.15)",
                        }}>
                          <div>
                            <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>{w.label}</div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "#fff" }}>{w.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* View toggle buttons — below weather HUD */}
                    <div style={{ position: "absolute", top: 54, right: 12, zIndex: 25, display: "flex", flexDirection: "column", gap: 6 }}>
                      <button onClick={() => setViewMode(VIEW.LIVE_MAP)} style={{
                        background: "rgba(255,255,255,0.94)", border: "0.5px solid #cbd5e1",
                        borderRadius: 7, padding: "5px 12px", fontSize: 11, fontWeight: 600,
                        cursor: "pointer", color: "#1e293b", display: "flex", alignItems: "center", gap: 5,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.13)",
                      }}>
                        📍 Show Live Map
                      </button>
                    </div>

                    {/* Path fetch bar (sitting on ship image) */}
                    <PathBar
                      startDate={startDate} endDate={endDate} todayStr={todayStr} maxEndDate={maxEndDate}
                      handleStartDate={handleStartDate} setEndDate={setEndDate}
                      fetchLoading={fetchLoading} fetchPath={fetchPath} fetchStatus={fetchStatus}
                      shipPath={shipPath} playing={playing} togglePlay={togglePlay}
                    />

                    {/* Playback controls */}
                    <PlaybackBar
                      shipPath={shipPath} playing={playing} togglePlay={togglePlay}
                      restartPb={restartPb} safeIdx={safeIdx} sliderSeek={sliderSeek}
                      curPoint={curPoint} speedIdx={speedIdx} cycleSpeed={cycleSpeed}
                    />
                  </div>
                )}

                {/* ══ LIVE MAP ══ */}
                {viewMode === VIEW.LIVE_MAP && (
                  <LiveMapView telemetry={telemetry} onShowShip={() => setViewMode(VIEW.SHIP)}/>
                )}

                {/* ══ PATH MAP ══ */}
                {viewMode === VIEW.PATH_MAP && (
                  <PathMapView
                    shipPath={shipPath} safeIdx={safeIdx} curPoint={curPoint}
                    visitedPath={visitedPath} remainingPath={remainingPath}
                    playing={playing} togglePlay={togglePlay} restartPb={restartPb}
                    sliderSeek={sliderSeek} speedIdx={speedIdx} cycleSpeed={cycleSpeed}
                    startDate={startDate} endDate={endDate} todayStr={todayStr} maxEndDate={maxEndDate}
                    handleStartDate={handleStartDate} setEndDate={setEndDate}
                    fetchLoading={fetchLoading} fetchPath={fetchPath} fetchStatus={fetchStatus}
                    onShowShip={() => setViewMode(VIEW.SHIP)}
                    pathFitTrigger={pathFitTrigger}
                  />
                )}
              </div>

              {/* DATA PANEL */}
              <aside style={{ width: 280, overflowY: "auto", background: "#fff", borderLeft: "0.5px solid #e2e8f0", flexShrink: 0 }}>
                <Section icon="⚙" title="Operational Status">
                  {telemetry ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      {shipPath.length > 0 && (
                        <div style={{ background: "#eff6ff", borderRadius: 8, padding: "6px 10px", border: "0.5px solid #bfdbfe", gridColumn: "1 / -1" }}>
                          <span style={{ ...CARDLBL, color: "#3b82f6" }}>Path Point</span>
                          <span style={{ fontSize: 12, fontWeight: 500, color: "#1e40af" }}>
                            {curPoint?.lat?.toFixed(4)}°, {curPoint?.lng?.toFixed(4)}°
                          </span>
                          {curPoint?.time && <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{curPoint.time}</div>}
                        </div>
                      )}
                      <div style={{ ...CARD, gridColumn: "1 / -1", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={CARDLBL}>Condition</span>
                        <span style={{ fontSize: 13, fontWeight: 500, color: "#16a34a" }}>{telemetry.navigation?.status || "—"}</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "#94a3b8", padding: "6px 0" }}>{telemetryLoading ? "Loading…" : "No data"}</div>
                  )}
                </Section>

                <Section icon="⚡" title="Live Performance">
                  <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>Main Engine</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
                    {[
                      ["Power", telemetry ? `${safeVal(telemetry.live_performance?.power_kw, 0)} KW` : "—", "#2563eb"],
                      ["RPM",   telemetry ? safeVal(telemetry.live_performance?.rpm, 1) : "—", "#2563eb"],
                      ["Load",  telemetry ? `${safeVal(telemetry.live_performance?.load_knm, 0)} kNm` : "—", "#2563eb"],
                    ].map(([l, v, c]) => (
                      <div key={l} style={CARD}>
                        <span style={CARDLBL}>{l}</span>
                        <span style={{ fontSize: 10, fontWeight: 500, color: c }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 8, marginBottom: 4 }}>Auxiliary Engine</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
                    {telemetry ? [
                      ["AE 1", telemetry.auxiliary_engine?.ae1_kw],
                      ["AE 2", telemetry.auxiliary_engine?.ae2_kw],
                      ["AE 3", telemetry.auxiliary_engine?.ae3_kw],
                    ].map(([label, kw]) => (
                      <div key={label} style={CARD}>
                        <span style={CARDLBL}>{label}</span>
                        <span style={{ fontSize: 10, fontWeight: 500, color: kw > 0 ? "#16a34a" : "#94a3b8" }}>{safeVal(kw, 0)} KW</span>
                      </div>
                    )) : (
                      <div style={{ ...CARD, gridColumn: "1 / -1" }}><span style={{ fontSize: 11, color: "#94a3b8" }}>No data</span></div>
                    )}
                  </div>
                  {telemetry?.fuel_consumption && (
                    <>
                      <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 8, marginBottom: 4 }}>Fuel Consumption</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
                        {[
                          ["ME Flow", telemetry.fuel_consumption.main_engine?.flow_rate],
                          ["ME Ctr",  telemetry.fuel_consumption.main_engine?.counter],
                          ["AE Flow", telemetry.fuel_consumption.aux_engine?.flow_rate],
                          ["AE Ctr",  telemetry.fuel_consumption.aux_engine?.counter],
                          ["AB Flow", telemetry.fuel_consumption.aux_boiler?.flow_rate],
                          ["AB Ctr",  telemetry.fuel_consumption.aux_boiler?.counter],
                        ].map(([label, val]) => (
                          <div key={label} style={CARD}>
                            <span style={CARDLBL}>{label}</span>
                            <span style={{ fontSize: 10, fontWeight: 500, color: "#b45309" }}>{safeVal(val, 1)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Section>
              </aside>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { box-shadow: 0 0 0 2px rgba(22,163,74,0.2); } 50% { box-shadow: 0 0 0 5px rgba(22,163,74,0.05); } }
        input[type=range] { height: 4px; }
        input[type=range]::-webkit-slider-thumb { width: 14px; height: 14px; border-radius: 50%; }
      `}</style>
    </React.Fragment>
  );
}